import { TypedDocumentNode, gql, useLazyQuery } from '@apollo/client';
import { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { Text16, Title4 } from '@sorare/core/src/atoms/typography';
import { ConnectPrivateWallet } from '@sorare/core/src/components/wallet/ConnectPrivateWallet';
import { useBlockchainContext } from '@sorare/core/src/contexts/blockchain';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import EmptyState from '../EmptyState';
import EthereumCard from '../EthereumCard';
import {
  EthereumTokensQuery,
  EthereumTokensQueryVariables,
} from './__generated__/index.graphql';

const ETHEREUM_TOKENS_QUERY = gql`
  query EthereumTokensQuery($address: String!) {
    ethereumCards(address: $address) {
      slug
      ...EthereumCard_anyCard
    }
  }
  ${EthereumCard.fragments.anyCard}
` as TypedDocumentNode<EthereumTokensQuery, EthereumTokensQueryVariables>;

const Container = styled(Vertical).attrs({ gap: 2, center: true })`
  width: 100%;
  text-align: center;
  justify-content: stretch;
`;
const Cards = styled.div`
  display: flex;
  gap: var(--double-unit);
  flex-wrap: wrap;
  justify-content: center;
  @media ${tabletAndAbove} {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
  }
`;

const Wallets = styled.div`
  align-self: center;
  /* min-width: 330px; */
  width: 100%;
`;

type Props = {
  closeDialog: () => void;
};

const DialogContainer = ({ closeDialog }: Props) => {
  const { isConnected, ethAddress } = useBlockchainContext();

  const [loadCards, { loading, data }] = useLazyQuery(ETHEREUM_TOKENS_QUERY);

  useEffect(() => {
    if (!isConnected || !ethAddress) return;

    loadCards({
      variables: { address: ethAddress },
    });
  }, [ethAddress, loadCards, isConnected]);

  if (!isConnected) {
    return (
      <Container>
        <Title4>
          <FormattedMessage
            id="DepositCard.Container.disconnectedTitle"
            defaultMessage="No Ethereum wallet connected"
          />
        </Title4>
        <Text16 color="var(--c-neutral-600)">
          <FormattedMessage
            id="DepositCard.Container.disconnectedDesc"
            defaultMessage="Connect your Ethereum wallet to deposit cards into your Sorare account"
          />
        </Text16>
        <Wallets>
          <ConnectPrivateWallet />
        </Wallets>
      </Container>
    );
  }

  if (!data || loading) return <LoadingIndicator />;

  const { ethereumCards } = data;

  return (
    <Container>
      {ethereumCards!.length === 0 ? (
        <EmptyState closeDialog={closeDialog} accountAddress={ethAddress} />
      ) : (
        <Cards>
          {ethereumCards!.map(card => (
            <EthereumCard key={card.slug} card={card} />
          ))}
        </Cards>
      )}
    </Container>
  );
};

export default DialogContainer;
